import React, { useState } from 'react';
import { Play, Pause, SkipForward, SkipBack, Volume2, VolumeX, Maximize, Award, Brain, CheckCircle } from 'lucide-react';
import ReactPlayer from 'react-player';

type VideoLesson = {
  id: string;
  title: string;
  description: string;
  duration: string;
  thumbnail: string;
  videoUrl: string;
  category: string;
  difficulty: 'principiante' | 'intermedio' | 'avanzado';
  xpReward: number;
  completed: boolean;
  keyPoints: string[];
};

const AIVideoLessons = () => {
  const [selectedVideo, setSelectedVideo] = useState<VideoLesson | null>(null);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);

  const mockLessons: VideoLesson[] = [
    {
      id: 'v1',
      title: 'Fundamentos de Product Management',
      description: 'Aprende los conceptos básicos del Product Management y cómo aplicarlos en tu día a día.',
      duration: '5:30',
      thumbnail: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      videoUrl: 'https://www.youtube.com/watch?v=example1',
      category: 'Product Management',
      difficulty: 'principiante',
      xpReward: 50,
      completed: false,
      keyPoints: [
        'Definición de Product Management',
        'Roles y responsabilidades',
        'Ciclo de vida del producto'
      ]
    },
    {
      id: 'v2',
      title: 'Investigación de Usuario Avanzada',
      description: 'Técnicas avanzadas para entender mejor a tus usuarios y sus necesidades.',
      duration: '8:15',
      thumbnail: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      videoUrl: 'https://www.youtube.com/watch?v=example2',
      category: 'User Research',
      difficulty: 'avanzado',
      xpReward: 100,
      completed: true,
      keyPoints: [
        'Entrevistas a profundidad',
        'Análisis cualitativo',
        'Mapas de empatía'
      ]
    }
  ];

  const handleVideoSelect = (video: VideoLesson) => {
    setSelectedVideo(video);
    setPlaying(true);
  };

  const togglePlay = () => {
    setPlaying(!playing);
  };

  const toggleMute = () => {
    setMuted(!muted);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    setMuted(newVolume === 0);
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'principiante':
        return 'bg-green-100 text-green-800';
      case 'intermedio':
        return 'bg-yellow-100 text-yellow-800';
      case 'avanzado':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-6 border-b">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Lecciones en Video</h2>
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <Brain className="h-5 w-5 text-accent mr-1" />
              <span className="text-sm font-medium text-gray-900">
                Aprendizaje Personalizado
              </span>
            </div>
            <div className="flex items-center">
              <Award className="h-5 w-5 text-yellow-500 mr-1" />
              <span className="text-sm font-medium text-gray-900">
                150 XP Ganados
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
        <div className="lg:col-span-2">
          {selectedVideo ? (
            <div className="space-y-4">
              <div className="relative aspect-video rounded-lg overflow-hidden">
                <ReactPlayer
                  url={selectedVideo.videoUrl}
                  width="100%"
                  height="100%"
                  playing={playing}
                  volume={volume}
                  muted={muted}
                  controls
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={togglePlay}
                    className="p-2 rounded-full hover:bg-gray-100"
                  >
                    {playing ? (
                      <Pause className="h-6 w-6 text-gray-700" />
                    ) : (
                      <Play className="h-6 w-6 text-gray-700" />
                    )}
                  </button>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={toggleMute}
                      className="p-2 rounded-full hover:bg-gray-100"
                    >
                      {muted ? (
                        <VolumeX className="h-6 w-6 text-gray-700" />
                      ) : (
                        <Volume2 className="h-6 w-6 text-gray-700" />
                      )}
                    </button>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0. 1"
                      value={volume}
                      onChange={handleVolumeChange}
                      className="w-24"
                    />
                  </div>
                </div>
                <button className="p-2 rounded-full hover:bg-gray-100">
                  <Maximize className="h-6 w-6 text-gray-700" />
                </button>
              </div>

              <div className="space-y-4">
                <h3 className="text-xl font-bold text-gray-900">{selectedVideo.title}</h3>
                <p className="text-gray-600">{selectedVideo.description}</p>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Puntos Clave</h4>
                  <ul className="space-y-2">
                    {selectedVideo.keyPoints.map((point, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2" />
                        <span className="text-gray-700">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full bg-gray-50 rounded-lg">
              <p className="text-gray-500">Selecciona un video para comenzar</p>
            </div>
          )}
        </div>

        <div className="lg:col-span-1">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Lista de Lecciones</h3>
          <div className="space-y-4">
            {mockLessons.map((lesson) => (
              <div
                key={lesson.id}
                className={`border rounded-lg p-4 cursor-pointer transition-all duration-300 ${
                  selectedVideo?.id === lesson.id
                    ? 'border-accent bg-accent/5'
                    : 'hover:border-accent/50 hover:shadow-sm'
                }`}
                onClick={() => handleVideoSelect(lesson)}
              >
                <div className="relative aspect-video rounded-lg overflow-hidden mb-3">
                  <img
                    src={lesson.thumbnail}
                    alt={lesson.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                    <Play className="h-8 w-8 text-white" />
                  </div>
                  <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                    {lesson.duration}
                  </div>
                </div>

                <h4 className="font-medium text-gray-900">{lesson.title}</h4>
                <p className="mt-1 text-sm text-gray-600 line-clamp-2">{lesson.description}</p>

                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDifficultyColor(
                        lesson.difficulty
                      )}`}
                    >
                      {lesson.difficulty}
                    </span>
                    <span className="text-xs text-gray-500">+{lesson.xpReward} XP</span>
                  </div>
                  {lesson.completed && (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIVideoLessons;